import React, { useState } from 'react';
import propTypes from 'prop-types';
import { cn } from 'lib/utils';
import useBrandLogo from 'hooks/useBrandLogo';
import { resolveLogoSrc } from 'helpers/brandLogo';

/**
 * renders the portal logo for the current session: an explicit src when given,
 * then the agency logo under sunshine, then the bundled brand asset. an image
 * that fails to load falls through to the next step instead of showing a broken icon
 * @param src - optional image tried first, typically an agent photo
 * @param variant - bundled slot to fall back to: 'logo', 'fullLogo' or 'fullLogoGrayScale'
 * @param tone - background this logo sits on: 'onLight' or 'onDark'
 * @param className - classes always applied
 * @param agencyClassName - extra classes applied only while the agency logo is the one rendered
 * @param alt - alt text, empty for decorative placements
 */
export default function BrandLogo({
  src = '',
  variant = 'logo',
  tone = 'onLight',
  className = '',
  agencyClassName = '',
  alt = ''
}) {
  const {
    src: brandSrc,
    fallbackSrc,
    isAgencyLogo
  } = useBrandLogo(variant, tone);
  // every url that failed, not just the last one: with a single value a broken
  // photo and a broken agency logo would keep handing the error back to each other
  const [failedSrcs, setFailedSrcs] = useState([]);

  const { resolved, usedPreferred } = resolveLogoSrc({
    src,
    brandSrc,
    fallbackSrc,
    failedSrcs
  });

  const showingAgencyLogo =
    isAgencyLogo && !usedPreferred && resolved === brandSrc;

  function handleError() {
    setFailedSrcs(previous => {
      if (previous.includes(resolved)) return previous;
      return [...previous, resolved];
    });
  }

  return (
    <img
      src={resolved}
      alt={alt}
      onError={handleError}
      // unlayered scss modules outrank tailwind utilities, so the agency logo
      // needs an inline style to keep its object-fit from being overridden
      style={showingAgencyLogo ? { objectFit: 'contain' } : undefined}
      className={cn(
        usedPreferred ? 'object-cover' : 'object-contain',
        className,
        showingAgencyLogo && agencyClassName
      )}
    />
  );
}

BrandLogo.propTypes = {
  src: propTypes.string,
  variant: propTypes.oneOf(['logo', 'fullLogo', 'fullLogoGrayScale']),
  tone: propTypes.oneOf(['onLight', 'onDark']),
  className: propTypes.string,
  agencyClassName: propTypes.string,
  alt: propTypes.string
};
